import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

const SOURCES = ["SAM.gov", "USASpending", "SBA DSBS", "LinkedIn", "Referral", "APEX/PTAC", "Industry Day", "Other"];

// IMPORTANT: Field is defined at MODULE scope (not inside the component).
// Defining it inside ProspectNew would create a new component type on every
// render → React would unmount + remount the <input> on every keystroke →
// active input would lose focus after each character typed. This was the
// bug fixed in the focus-loss regression.
function Field({ label, name, value, onChange, placeholder, area, type = "text" }) {
  return (
    <div className={area ? "sm:col-span-2" : ""}>
      <label className="tick-label block mb-1.5">{label}</label>
      {area ? (
        <textarea
          data-testid={`field-${name}`}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={3}
          className="w-full bg-[var(--bg-secondary)] border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm p-3 font-mono"
        />
      ) : (
        <input
          data-testid={`field-${name}`}
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full bg-[var(--bg-secondary)] border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm p-3 font-mono"
        />
      )}
    </div>
  );
}

const EMPTY_FORM = {
  company_name: "", website: "", primary_naics: "", secondary_naics: "",
  certifications: "", agency_focus: "", recent_awards: "", estimated_size: "",
  decision_maker_name: "", decision_maker_title: "", decision_maker_email: "", decision_maker_linkedin: "",
  notes: "", reason_govbidiq_helps: "",
  source_type: "Other", source_detail: "",
};

const csv = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

export default function ProspectNew() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // Stable handler: single onChange wired via `name` attribute so we don't
  // create new closures per-field on every render.
  const onField = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) {
      toast.error("Company name required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        certifications: csv(form.certifications),
        agency_focus: csv(form.agency_focus),
        recent_awards: csv(form.recent_awards),
      };
      const r = await api.post("/prospects", payload);
      toast.success("Prospect created");
      nav(`/internal/prospects/${r.data.prospect_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="prospect-new">
      <div className="tick-label text-sky-400 mb-2">/ New Prospect</div>
      <h1 className="font-display font-black text-3xl text-slate-100 mb-6">Build a Prospect Intelligence Card</h1>

      <form onSubmit={submit} className="space-y-6 max-w-4xl">
        <div className="panel p-6">
          <h2 className="font-display font-bold text-slate-100 mb-4">Source</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="tick-label block mb-1.5">Source Type</label>
              <select
                data-testid="field-source_type"
                name="source_type"
                value={form.source_type}
                onChange={onField}
                className="w-full bg-[var(--bg-secondary)] border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm p-3 font-mono"
              >
                {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <Field label="Source Detail" name="source_detail" value={form.source_detail} onChange={onField} placeholder="Notes about where this lead came from" />
          </div>
        </div>

        <div className="panel p-6">
          <h2 className="font-display font-bold text-slate-100 mb-4">Company</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Company Name *" name="company_name" value={form.company_name} onChange={onField} placeholder="Acme Federal Solutions" />
            <Field label="Website" name="website" value={form.website} onChange={onField} placeholder="acme.com" />
            <Field label="Primary NAICS" name="primary_naics" value={form.primary_naics} onChange={onField} placeholder="541512" />
            <Field label="Secondary NAICS (comma sep)" name="secondary_naics" value={form.secondary_naics} onChange={onField} placeholder="541330, 541611" />
            <Field label="Certifications (comma sep)" name="certifications" value={form.certifications} onChange={onField} placeholder="SDVOSB, HUBZone, 8(a), CMMC L2" />
            <Field label="Agency Focus (comma sep)" name="agency_focus" value={form.agency_focus} onChange={onField} placeholder="DoD, VA, NASA" />
            <Field label="Recent Awards (comma sep)" name="recent_awards" value={form.recent_awards} onChange={onField} placeholder="USACE $4M 2024, DISA $1.2M 2023" area />
            <Field label="Estimated Size" name="estimated_size" value={form.estimated_size} onChange={onField} placeholder="35 employees · $8M revenue" />
          </div>
        </div>

        <div className="panel p-6">
          <h2 className="font-display font-bold text-slate-100 mb-4">Decision Maker</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name" name="decision_maker_name" value={form.decision_maker_name} onChange={onField} placeholder="Jane Doe" />
            <Field label="Title" name="decision_maker_title" value={form.decision_maker_title} onChange={onField} placeholder="VP Federal BD" />
            <Field label="Email" name="decision_maker_email" value={form.decision_maker_email} onChange={onField} placeholder="jane@acme.com" type="email" />
            <Field label="LinkedIn" name="decision_maker_linkedin" value={form.decision_maker_linkedin} onChange={onField} placeholder="linkedin.com/in/janedoe" />
          </div>
        </div>

        <div className="panel p-6">
          <h2 className="font-display font-bold text-slate-100 mb-4">Qualification &amp; Notes</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Reason GovBidIQ May Help" name="reason_govbidiq_helps" value={form.reason_govbidiq_helps} onChange={onField} placeholder="They bid 30+ RFPs/year with limited capture support..." area />
            <Field label="Notes" name="notes" value={form.notes} onChange={onField} placeholder="Any other context" area />
          </div>
        </div>

        <button
          type="submit"
          disabled={busy}
          data-testid="prospect-submit"
          className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-5 py-3"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create Prospect <ArrowRight className="w-4 h-4" strokeWidth={3} /></>}
        </button>
      </form>
    </div>
  );
}
